import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useRouteState } from "@/hooks/useRouteState";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { Globals } from "@coreapp/CoreApp/Globals";
import { LocationSource } from "@coreapp/CoreApp/LocationSource";
import { appstr } from "@coreapp/utils/appstr";
import { SubscribeExternalModuleReactAPI } from "@coreapp/main";
import { handleResize } from "@coreapp/utils/handleResize";
import { useTopic } from "@/hooks/useTopic";
import { isNullish } from "@theorgchartdev/storybook";
import { CHART_QUERY_PARAM_KEYS, routePaths } from "@/appComponents/routes";
import { isPhoneLayoutFromCSS } from "@/Utils/getPhoneLayout";

export const PlanSettingsSectionNames = {
  General: "General",
  Approvals: "Approvals"
};

export const PlanSettingsSectionRoutes = {
  General: "general",
  Approvals: "approvals"
};

export const enumPlanSettings = {
  0: "General",
  1: "Approvals"
};

export const ChartSettingSectionNames = {
  DataSource: "Data sources",
  Mapping: "Mapping",
  Presentation: "Presentation",
  SearchAndDirectory: "Search & Directory",
  Profile: "Profile",
  FieldsAndFormulas: "Fields & Formulas",
  ImportFilters: "Import Filters",
  Subcharts: "Subcharts",
  Refresh: "Refresh",
  PointInTime: "Point in time"
};
export const ChartSettingsSectionRoutes = {
  DataSource: "data-source",
  Mapping: "mapping",
  Presentation: "presentation",
  SearchAndDirectory: "search-directory",
  Profile: "profile",
  FieldsAndFormulas: "fields-formulas",
  ImportFilters: "import-filters",
  Subcharts: "subcharts",
  Refresh: "refresh",
  PointInTime: "point-in-time"
};

export const getChartSettingsTestPrefix = slug => `chart-settings-${slug}`;

export const enumChartSettings = {
  0: "DataSource",
  1: "Mapping",
  2: "Presentation",
  3: "SearchAndDirectory",
  4: "Profile",
  5: "FieldsAndFormulas",
  6: "ImportFilters",
  7: "Subcharts",
  8: "Refresh",
  9: "PointInTime"
};

export const sectionsRequireRefresh = [
  "import",
  "append",
  "merge",
  "mapping",
  "directory",
  "fields",
  "fieldFormats",
  "formulas",
  "highlights",
  "keys",
  "localization",
  "pointintime"
];

const ChartSubSettings = {};
for (const key in enumChartSettings) {
  ChartSubSettings[key] = [];
}
ChartSubSettings[5].push("CreateFormula");
export { ChartSubSettings };

export const AccountSettingSectionNames = {
  General: "General",
  BaseCharts: "Base charts",
  Theme: "Theme",
  AccessGroups: "Access groups",
  ManageUsers: "Manage users",
  UIProfiles: "UI profiles",
  Security: "Security",
  Authorization: "Authorization",
  Checkpoints: "Checkpoints",
  AuditLog: "Audit log"
};

export const AccountSettingsSectionRoutes = {
  General: "general",
  BaseCharts: "base-charts",
  Theme: "theme",
  AccessGroups: "access-groups",
  ManageUsers: "manage-users",
  UIProfiles: "ui-profiles",
  Security: "security",
  Authorization: "authorization",
  Checkpoints: "checkpoints",
  AuditLog: "audit-log"
};

export const getAccountSettingsTestPrefix = slug => `account-settings-${slug}`;

export const enumAccountSettings = {
  0: "General",
  1: "BaseCharts",
  2: "Theme",
  3: "AccessGroups",
  4: "ManageUsers",
  5: "UIProfiles",
  6: "Security",
  7: "Authorization",
  8: "Checkpoints",
  9: "AuditLog"
};

export const DetailSectionsRoutes = {
  ApplicationDetails: "application",
  AccountDetails: "account",
  UserDetails: "user",
  ChartDetails: "chart"
};

export const DetailSectionNames = {
  ApplicationDetails: "Application",
  AccountDetails: "account",
  UserDetails: "User",
  ChartDetails: "Chart"
};
export const enumDetailSettings = {
  0: "ApplicationDetails",
  1: "AccountDetails",
  2: "UserDetails",
  3: "ChartDetails"
};

export const ChartExplorerTabs = {
  RECENTLY_OPENED: 0,
  BASE_CHARTS: 1,
  FILES: 2,
  PLANS: 3,
  POINT_IN_TIME: 4
};
const NavigationContext = React.createContext();

const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();
  const nav = useAppNavigate();
  const { chartExplorer: chartExplorerParam, setParam: setRouteParam } = useRouteState();

  const [showProfileCard, setShowProfileCard] = React.useState({
    show: false,
    target: null
  });

  const [isPhoneLayout, setIsPhoneLayout] = React.useState(false);

  React.useLayoutEffect(() => {
    const appContainer = document.querySelector(".appContainer");
    if (!appContainer) return undefined;

    let latched = false;
    let pendingFrame = null;

    const cancelPendingFrame = () => {
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
        pendingFrame = null;
      }
    };

    let pendingResizeFrame = null;
    const cancelPendingResizeFrame = () => {
      if (pendingResizeFrame !== null) {
        cancelAnimationFrame(pendingResizeFrame);
        pendingResizeFrame = null;
      }
    };

    // Check now so the user never sees desktop chrome flash before switching to phone.
    setIsPhoneLayout(isPhoneLayoutFromCSS());

    const scheduleResize = () => {
      if (pendingResizeFrame !== null) return;
      pendingResizeFrame = requestAnimationFrame(() => {
        pendingResizeFrame = null;
        if (Globals._app?.fWSMgr) handleResize();
      });
    };

    const observer = new ResizeObserver(entries => {
      if (latched) {
        scheduleResize();
        return;
      }

      cancelPendingFrame();

      const entry = entries[0];
      const box = entry.contentBoxSize?.[0];
      const width = box ? box.inlineSize : entry.contentRect.width;
      const height = box ? box.blockSize : entry.contentRect.height;

      if (width === 0 && height === 0) return;

      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        latched = true;
        setIsPhoneLayout(isPhoneLayoutFromCSS());

        // Defer to the next frame so handleResize measures the DOM after the
        // isPhoneLayout commit (nav/sidebar mount change) lands, not before it.
        scheduleResize();
      });
    });

    observer.observe(appContainer);
    return () => {
      cancelPendingFrame();
      cancelPendingResizeFrame();
      observer.disconnect();
    };
  }, []);

  // URL-driven: ?chartexplorer is the single source of truth. Initial state reads
  // the URL so deep links like /base/123?chartexplorer open the modal on mount.
  const [showChartExplorer, setShowChartExplorer] = React.useState(() =>
    new URLSearchParams(LocationSource.getSearch()).has("chartexplorer")
  );
  const [explorerLaunchTab, setExplorerLaunchTab] = React.useState(false);

  useTopic("LaunchChartExplorer", (tab = ChartExplorerTabs.RECENTLY_OPENED) => {
    openChartExplorer(tab);
  });

  const OpenPlanChart = selectedPlan => {
    if (selectedPlan == null) return;
    // The clean URL from toPlan already lacks ?chartexplorer, so the
    // URL → state effect closes the modal automatically. Don't call
    // closeChartExplorer() — it reads stale search params and re-applies them.
    nav.toPlan(selectedPlan);
  };

  const PrepEnterPlanning = () => {
    const doc = GetDoc();
    const wsMgr = GetWSMgr();
    if (Globals._svrChartMgr.GetLastPlanID() != null && !doc.IsPlanChart()) {
      var headerText = appstr("Resume last open plan");
      var yesStr = appstr("Continue Planning");
      var noStr = appstr("Plan Explorer");
      var infoText = appstr("Do you want to resume planning in your last open plan?");
      wsMgr.CreateReactAlert(
        headerText,
        infoText,
        () => OpenPlanChart(Globals._svrChartMgr.GetLastPlanID()),
        () => openChartExplorer(ChartExplorerTabs.PLANS),
        yesStr,
        noStr
      );
    } else {
      openChartExplorer(ChartExplorerTabs.PLANS);
    }
  };

  const GetUIProfile = () => {
    const wsMgr = GetWSMgr();
    const accessGroupMgr = wsMgr?.GetAccessGroupMgr();
    const accessGroup = wsMgr?.fUserInfo.GetUserGroupID();
    if (accessGroup == 0) {
      return "default";
    } else {
      return accessGroupMgr?.fGroupsClone[accessGroup].uiprofile;
    }
  };

  const isValidExplorerTab = checkTab => {
    return !isNullish(checkTab) && Object.values(ChartExplorerTabs).includes(checkTab);
  };

  const openChartExplorer = React.useCallback(
    launchTab => {
      if (showChartExplorer) return;

      const startingTab = isValidExplorerTab(launchTab)
        ? launchTab
        : ChartExplorerTabs.RECENTLY_OPENED;

      if (Globals._uiConfig.fChartExplorer) {
        const wsMgr = GetWSMgr();
        const eventTracker = wsMgr.GetUsageTracker();
        eventTracker.ReportEvent("chart_explorer.explorer.opened");
        // Launch tab is local UI state — the URL flag is just the open/closed signal.
        // URL-driven: write ?chartexplorer=true and let the URL → state effect open the
        // modal. Using "true" (not "") matches the ?create=true convention and avoids
        // the trailing-equals form (?chartexplorer=) that URLSearchParams produces for
        // empty string values.
        // Always set (not `if (startingTab)`): a blocked+cancelled open otherwise
        // leaves a stale launch tab that hijacks the next open.
        setExplorerLaunchTab(startingTab);
        setRouteParam("chartexplorer", "true");
      } else {
        const wsMgr = GetWSMgr();
        const uiProfileName = GetUIProfile();
        wsMgr.CreateReactAlert(
          appstr("Permission denied"),
          appstr("chartExplorerWarn") + " (" + uiProfileName + ") " + appstr("contactAdmin"),
          () => {},
          undefined,
          appstr("OK"),
          undefined,
          {}
        );
      }
    },
    [showChartExplorer, setRouteParam]
  );

  const closeChartExplorer = React.useCallback(() => {
    // URL-driven: clear ?chartexplorer; the URL → state effect closes the modal.
    setExplorerLaunchTab(false);
    setRouteParam("chartexplorer", null);
  }, [setRouteParam]);

  // URL → state sync: ?chartexplorer is the single source of truth for the modal's
  // open/closed state. Deep link, back/forward nav, and any external nav converge
  // through this effect. Guard against loops by comparing against current state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (chartExplorerParam === showChartExplorer) return;
    setShowChartExplorer(chartExplorerParam);
    if (!chartExplorerParam) setExplorerLaunchTab(false);
  }, [chartExplorerParam]);

  const LaunchUIElement = React.useCallback((uiElement, bool, payload = {}) => {
    if (uiElement === "profile") {
      setShowProfileCard({
        target: null,
        ignoreSelection: false,
        ...payload,
        show: bool
      });
    }
  }, []);

  const contextValue = React.useMemo(
    () => ({
      showProfileCard,
      isPhoneLayout,
      chartExplorer: {
        showChartExplorer,
        explorerLaunchTab,
        openChartExplorer,
        closeChartExplorer
      },
      planning: {
        OpenPlanChart,
        PrepEnterPlanning
      },
      LaunchUIElement
    }),
    [
      showProfileCard,
      isPhoneLayout,
      showChartExplorer,
      explorerLaunchTab,
      openChartExplorer,
      closeChartExplorer
    ]
  );

  // Coreapp → React bridge for entering the box layout editor. Called by
  // wsMgr.DoBoxLayoutEditor when the user opens BLE from canvas. Reads
  // window.location.search AT CALL TIME (imperative, fresh) rather than
  // React's useLocation (reactive but captured stale by the mount-time
  // subscribe). Carries CHART_QUERY_PARAM_KEYS passengers so the round
  // trip can restore the canvas session on exit. Uses raw `navigate`
  // (router-bound, stable across renders) — going through
  // nav.toBoxLayout would re-capture useLocation on a stale render.
  const handleNavToBoxLayout = React.useCallback(
    (set, id) => {
      const currentSearch = new URLSearchParams(LocationSource.getSearch());
      const params = new URLSearchParams();
      for (const key of CHART_QUERY_PARAM_KEYS) {
        // ?display= (viewmanager/directory) is transient overlay state, not a BLE passenger.
        if (key === "display") continue;
        const val = currentSearch.get(key);
        if (val !== null) params.set(key, val);
      }
      const s = params.toString();
      const path = routePaths.boxlayout(set, id);
      navigate(s ? `${path}?${s}` : path);
    },
    [navigate]
  );

  // Coreapp → React bridge for exiting the box layout editor. Called by
  // wsMgr.DoExitBoxLayoutEditor when the user clicks Cancel / Save changes
  // from inside BLE. Reads canvas-context passenger params off the current
  // /boxlayout/ URL and constructs the target canvas route from doc
  // identity at call time — mirrors useAppNavigate.toCurrentResource, but
  // imperative (fresh) to match the coreapp-bridge contract and avoid the
  // stale-closure hazard that comes from capturing React's nav object.
  const handleExitBoxLayout = React.useCallback(
    (extraParams = {}) => {
      const wsMgr = GetWSMgr();
      const doc = wsMgr?.GetDoc?.();
      if (!doc) return;

      const currentSearch = new URLSearchParams(LocationSource.getSearch());
      const params = new URLSearchParams();
      for (const key of CHART_QUERY_PARAM_KEYS) {
        const val = currentSearch.get(key);
        if (val !== null) params.set(key, val);
      }
      // Surface-opening params (e.g. display=directory) carried from an in-BLE
      // overlay click so it opens on the chart route, not over a torn-down BLE.
      for (const [key, val] of Object.entries(extraParams)) {
        if (val != null) params.set(key, val);
      }
      const s = params.toString();
      const suffix = s ? `?${s}` : "";

      const docMgr = wsMgr.GetDocMgr?.();
      const planMgr = wsMgr.GetPlanMgr?.();
      let target = null;
      // planID 0 is valid (first plan on a fresh org) — test != null, not truthiness.
      if (planMgr?.GetPlanID?.() != null) {
        target = routePaths.plan(planMgr.GetPlanID());
      } else if (doc.IsServerChart?.() && doc.GetChartID?.()) {
        target = routePaths.base(String(doc.GetChartID()));
      } else {
        const fileName = docMgr?.GetFileName?.(true);
        const directory = docMgr?.GetFileDirectory?.();
        if (fileName && directory) {
          target = routePaths.open(directory, fileName);
        } else if (doc.GetChartID?.()) {
          // Unsaved custom chart has no filename/directory yet, so fall back to the base
          // chart identity; create=true rides in params so the edit session resumes on arrival.
          target = routePaths.base(String(doc.GetChartID()));
        }
      }
      if (!target) return;
      navigate(`${target}${suffix}`);
    },
    [navigate]
  );

  React.useEffect(() => {
    SubscribeExternalModuleReactAPI(
      {
        navigate,
        setSearchParams: nav.setSearchParams,
        uiElementSetter: LaunchUIElement,
        navToBoxLayout: handleNavToBoxLayout,
        exitBoxLayout: handleExitBoxLayout
      },
      "uiState"
    );
  }, []);

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};

export const useNavigationContext = () => React.useContext(NavigationContext);

export default NavigationProvider;
